import React from 'react';
import { Box, Container, Typography, Grid, Stack, Divider } from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import logo from '../assets/images/Inspect IQ Logo.svg';
import { trackContactClick, trackOutbound } from '../utils/analytics';

const quickLinks = [
  { text: 'Home', path: '/' },
  { text: 'Services', path: '/services' },
  { text: 'Platform', path: '/platform' },
  { text: 'About Us', path: '/about' },
  { text: 'Careers', path: '/careers' },
  { text: 'Contact', path: '/contact' },
];

const linkSx = {
  fontSize: '0.9rem',
  color: 'rgba(255,255,255,0.75)',
  textDecoration: 'none',
  transition: 'color 0.2s ease',
  '&:hover': { color: '#fff' },
};

const Footer = () => {
  return (
    <Box component="footer" sx={{ bgcolor: 'primary.dark', color: 'white', pt: { xs: 5, md: 7 }, pb: 3 }}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Brand */}
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Box
                component="img"
                src={logo}
                alt="InspectIQ"
                sx={{ height: 36, filter: 'brightness(0) invert(1)' }}
              />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
                InspectIQ
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mb: 1, maxWidth: 320 }}>
              Independent marine, cargo and container inspection services worldwide,
              with operational bases in India — delivering accurate, evidence-based assurance.
            </Typography>
            <Typography variant="caption" sx={{ color: 'secondary.light', fontWeight: 600, letterSpacing: '0.05em' }}>
              Expert inspections. Trusted results.
            </Typography>
          </Grid>

          {/* Quick links */}
          <Grid item xs={6} md={2}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#fff' }}>
              Company
            </Typography>
            <Stack spacing={1.2}>
              {quickLinks.map((link) => (
                <Box key={link.text} component={RouterLink} to={link.path} sx={linkSx}>
                  {link.text}
                </Box>
              ))}
              <Box
                component="a"
                href="https://imdg.inspectiq.in/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOutbound('https://imdg.inspectiq.in/', 'imdg_footer')}
                sx={linkSx}
              >
                IMDG Search
              </Box>
            </Stack>
          </Grid>

          {/* Contact */}
          <Grid item xs={6} md={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#fff' }}>
              Get in Touch
            </Typography>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.2} alignItems="center">
                <PhoneIcon sx={{ fontSize: 18, color: 'secondary.light' }} />
                <Box component="a" href="tel:+919574449544" onClick={() => trackContactClick('phone', '+919574449544')} sx={linkSx}>
                  +91 95744 49544
                </Box>
              </Stack>
              <Stack direction="row" spacing={1.2} alignItems="center">
                <EmailIcon sx={{ fontSize: 18, color: 'secondary.light' }} />
                <Box component="a" href="mailto:info@inspectiq.in" onClick={() => trackContactClick('email', 'info@inspectiq.in')} sx={linkSx}>
                  info@inspectiq.in
                </Box>
              </Stack>
            </Stack>
          </Grid>

          {/* Offices */}
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#fff' }}>
              Our Offices
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.2}>
                <LocationIcon sx={{ fontSize: 18, color: 'secondary.light', mt: 0.3 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                    Vadodara
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Sayaji Park Society, Ajwa Rd, Gujarat 390019
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.15)' }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
            © {new Date().getFullYear()} InspectIQ Private Limited. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={3}>
            <Box component={RouterLink} to="/privacy-policy" sx={{ ...linkSx, fontSize: '0.8rem' }}>
              Privacy Policy
            </Box>
            <Box component={RouterLink} to="/terms" sx={{ ...linkSx, fontSize: '0.8rem' }}>
              Terms of Service
            </Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
